import React, { useState, useMemo, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import { TextContent } from '_ui-core-js/React/components/TextContent.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { GalleryGrid } from '_ui-core-js/React/components/GalleryGrid.jsx';
import { multiplier } from '_ui-core-js/utilities/multiplier.js';
import { randomNumber } from '_ui-core-js/utilities/randomNumber.js';
import { isOddNumber } from '_ui-core-js/utilities/isOddNumber.js';
import { VIEWS } from './AiExperience.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities//fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function ColorPalettePresentationScreen(props) {

  const { block, data, state } = props;
  const { title, subtitle, copy, ctas } = data;
  const [colorIndexes, setColorIndexes] = useState(state.colorPaletteHexValuesSelectedIndexes);
  const isMountedRef = useRef();

  const selectedImages = useMemo(() => state.selectedColorPaletteImages.map((imgObj, index) => ({ 
    url: imgObj.url, 
    swatchColor: state.colorPaletteHexValues[index][colorIndexes[index]],
    swatchPosition: isOddNumber(index) ? 'right' : 'left'
  })), [colorIndexes]);

  function randomisePalette() {
    return [...Array(4).fill('')].map((item, index) => randomNumber(0, state.colorPaletteHexValues[index].length-1));
  };

  function proceedToUserChoicesSelection() {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.clickCta,
      label: CORE_GTM_TRACKING_VALUES.label.confirmPalette
    });

    state.setColorPaletteHexValuesSelectedIndexes(colorIndexes);
    state.setColorPalettePayload(state.colorPaletteHexValues.map((values, index) => values[colorIndexes[index]]));
    state.setActiveView(VIEWS.userChoicesSelection.name);
  };

  const colorSwatchOverlay = (image, index) => (
    <div className={`${block}__color-swatch-overlay ${block}__color-swatch-overlay--${image.swatchPosition}`}>
      <CssAnimation 
      enter={{name: 'scale-up-0-1', duration: 1.25, delay: '0.5, hidden'}}
      triggerEnter={true}
      >
        <div style={{background: image.swatchColor}}></div>
      </CssAnimation>
    </div>
  );

  const colorPallete = () => (
    <ul className={`${block}__color-palette`}>
      {
        state.colorPaletteHexValues.map((value, index) => (
          <CssAnimation 
          enter={{name: 'vertical-top-bottom-clip', duration: 0.75, easing: 'ease-out', delay: `${multiplier(index, 0.20, 0.75)}, hidden`}}
          triggerEnter={true}
          disable={isMountedRef.current}
          key={value}
          >
            <li 
            className={`${block}__color-palette-item`} 
            style={{background: value[colorIndexes[index]]}}>
            </li>
          </CssAnimation>
        ))
      }
    </ul>
  );

  useEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.workflowEvent,
      componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: CORE_GTM_TRACKING_VALUES.action.workflowStep,
      label: CORE_GTM_TRACKING_VALUES.label.paletteScreen
    }, {
      workflow: {
        name: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience,
        step: CORE_GTM_TRACKING_VALUES.label.paletteScreen,
        conversion: 0,
        variant: (state.loggedIn && state.instagramCode) ? 'instagram' : 'not-connected'
      }
    });

    isMountedRef.current = true;
  }, []);

  return (
    <div className={`${block}__color-palette-presentation-screen ${block}__layout--horizontal container-xxxl`}>
      <div className='row'>
        <div className={`${block}__layout--block-one col-12 col-md-5 col-lg-4`}>
          <TextContent 
          heading={title}
          subheading={subtitle}
          body={copy}
          modifier='centered-mobile'
		  cssAnimationTrigger={true}
          />
          { 
            state.smallView && 
              colorPallete() 
          }
          <div className={`${block}__cta-container`}>
            <Button 
            label={ctas.next.label} 
            type='primary' 
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDelay={1} 
            cssAnimationDuration={0.5}
            onClickCallback={proceedToUserChoicesSelection}
            />
            <Button 
            label={ctas.refresh.label}
            type='secondary' 
            icon='refresh'
            cssAnimation={true} 
            cssAnimationName='vertical-top-bottom-clip'
            cssAnimationTrigger={true}
            cssAnimationDelay={1.5}
            cssAnimationDuration={0.5}
            onClickCallback={() => {
              fireGTMTrackingEvent({
                name: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
                componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
                componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                action: CORE_GTM_TRACKING_VALUES.action.clickCta,
                label: CORE_GTM_TRACKING_VALUES.label.refreshColours
              });

              setColorIndexes(randomisePalette())}
            }
            />
            <Button 
            label={ctas.back.label}
            type='tertiary' 
            icon='back'
            slim={true}
            cssAnimation={true} 
            cssAnimationTrigger={true}
            cssAnimationDelay={2}
            cssAnimationDuration={0.5}
            onClickCallback={() => {
              fireGTMTrackingEvent({
                name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
                componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceAiExperience, 
                componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                action: CORE_GTM_TRACKING_VALUES.action.previousStep,
                label: CORE_GTM_TRACKING_VALUES.label.paletteScreen
              });

              state.setActiveView(VIEWS.colorPaletteSelection.name)}
            }
            />
          </div>
        </div>
        <div className={`${block}__layout--block-two col-12 col-md-6 offset-md-1 offset-lg-2`}>
          <GalleryGrid
          images={selectedImages}
          blockModifier='fixed-2-col'
          useImageAspectRatio={false}
          imageOverlay={colorSwatchOverlay}
          />
          { 
            !state.smallView && 
              colorPallete() 
          }
        </div>
      </div>
    </div>
  )
};

ColorPalettePresentationScreen.propTypes = {
  block: propTypes.string.isRequired,
  state: propTypes.object.isRequired,
  data: propTypes.object.isRequired
};